<script module lang="ts">
  type AnimationOption = {
    name: string;
    spring: number;
    ms: number;
  };

  export const animationSpeedOptions: Record<number, AnimationOption> = {
    1: {
      name: 'really slow',
      spring: 15,
      ms: 3000,
    },
    2: {
      name: 'slow',
      spring: 30,
      ms: 1500,
    },
    3: {
      name: 'medium',
      spring: 60,
      ms: 750,
    },
    4: {
      name: 'fast',
      spring: 90,
      ms: 500,
    },
    5: {
      name: 'really fast',
      spring: 120,
      ms: 333,
    },
  };

  export const settings = {
    animationSpeed: new PersistedState<number>('settings.animation-speed', 3),
    shadows: new PersistedState('settings.shadows', false),
    confetti: new PersistedState('settings.confetti', true),
    alphaScrollBar: new PersistedState('settings.alpha-scroll-bar', false),
    wordTimestamps: new PersistedState('settings.word-timestamps', true),
    debug: new PersistedState('settings.debug', false),
  };

  const settingsModal = new ModalQueryState('settings');
</script>

<script lang="ts">
  import SlidersIcon from '~icons/feather/sliders';
  import Dark from '~icons/mdi/brightness-3';
  import Shimmer from '~icons/mdi/shimmer';
  import Speed from '~icons/mdi/speedometer-slow';
  import Shadow from '~icons/mdi/box-shadow';
  import Alphabetical from '~icons/mdi/alphabetical';
  import Clock from '~icons/mdi/clock-outline';
  import CloudSync from '~icons/mdi/cloud-sync';
  import Close from '~icons/mdi/close';
  import ActionButton from '~/components/ActionButton.svelte';
  import Modal from '~/components/Modal.svelte';
  import { ModalQueryState } from '~/lib/nav.svelte';
  import { theme, toggleTheme } from '~/theme/darkMode.svelte';
  import { dev } from '$app/env';
  import { PersistedState } from 'runed';
  import Slider from '~/components/Slider.svelte';
  import Toggle from '~/components/Toggle.svelte';
  import { fly } from 'svelte/transition';
  import { page } from '$app/state';
  import { syncEnabled } from '~/routes/classic/SyncModal.svelte';
  import { premiumModal } from '~/modals/PremiumModal.svelte';
  import Premium from '~/icons/Premium.svelte';

  type Props = {
    iconOnly?: boolean;
  };
  const props: Props = $props();

  const handleOpen = () => {
    settingsModal.push();
  };

  const handleClose = () => {
    settingsModal.pop();
  };
</script>

<ActionButton iconOnly={props.iconOnly} class="settings" onClick={handleOpen}>
  <SlidersIcon />
  Settings
</ActionButton>
<Modal index={2} open={settingsModal.open} onClose={handleClose}>
  {#snippet title()}
    <SlidersIcon />
    Settings
  {/snippet}
  {#snippet content()}
    <div class="content">
      <div class="setting">
        <label for="theme">
          <Dark />
          Dark mode
        </label>
        <Toggle id="theme" checked={theme.current === 'dark'} onchange={toggleTheme} />
        <p>This toggles the application theme between light and dark mode.</p>
      </div>
      <div class="setting" class:disabled={!page.data.isPremium}>
        <label for="cloud-sync">
          <CloudSync />
          Cloud sync
        </label>
        {#if page.data.isPremium}
          <Toggle id="cloud-sync" bind:checked={syncEnabled.current} />
          <p>Automatically save your classic game to the cloud and restore it on other devices.</p>
        {:else}
          <ActionButton outlink={false} href="?premium=t#sort">Premium <Premium size="1.25em" /></ActionButton>
          <p>Automatically save your classic game to the cloud and restore it on other devices.</p>
        {/if}
      </div>
      <div class="setting">
        <label for="animation-speed">
          <Speed />
          Animation speed
        </label>
        <span class="speed">
          {#key settings.animationSpeed.current}
            <span in:fly={{ y: -16 }} out:fly={{ y: 16 }}>
              {animationSpeedOptions[settings.animationSpeed.current].name}
            </span>
          {/key}
        </span>
        <div class="slider">
          <Slider
            id="animation-speed"
            value={settings.animationSpeed}
            min={1}
            max={5}
            step={1}
          />
        </div>
        <p>
          This changes the speed of animations such as swapping tiles, stars,
          and the gauge.
        </p>
      </div>
      <div class="setting">
        <label for="confetti">
          <Shimmer />
          Confetti
        </label>
        <Toggle id="confetti" bind:checked={settings.confetti.current} />
        <p>
          This turns on or off the confetti animation that plays after scoring
          high-point words. Enabling can cause slowdowns on older devices.
        </p>
      </div>
      <div class="setting">
        <label for="alpha-scroll-bar">
          <Alphabetical />
          Alphabet bar
        </label>
        <Toggle id="alpha-scroll-bar" bind:checked={settings.alphaScrollBar.current} />
        <p>
          Show an alphabetical scroll bar to quickly find matching letters on the board in
          classic mode.
        </p>
      </div>
      <div class="setting">
        <label for="word-timestamps">
          <Clock />
          Word timestamps
        </label>
        <Toggle id="word-timestamps" bind:checked={settings.wordTimestamps.current} />
        <p>
          Show how long ago each word was found in the word history.
        </p>
      </div>
      <div class="setting">
        <label for="shadows">
          <Shadow />
          Shadows
        </label>
        <Toggle id="shadows" bind:checked={settings.shadows.current} />
        <p>
          This turns on or off the shadows behind words as they fly off the
          board. Enabling can cause slowdowns on older devices.
        </p>
      </div>
      {#if dev}
        <div class="setting">
          <label for="debug"> Debug </label>
          <Toggle id="debug" bind:checked={settings.debug.current} />
          <p>Toggle debug overlay</p>
        </div>
        <ActionButton
          onClick={() => {
            throw new Error('help');
          }}>Help</ActionButton
        >
      {/if}
      <span class="version">
        version: {localStorage.getItem('version')?.slice(0, 8)}</span
      >
    </div>
  {/snippet}
  {#snippet controls()}
    <ActionButton onClick={handleClose}><Close />Close</ActionButton>
  {/snippet}
</Modal>

<style lang="scss">
  @use '~/theme/include-media' as *;
  @use '~/theme/themify' as *;
  .content {
    display: grid;
    grid-auto-flow: row;
    row-gap: 1em;
    @include media('>=540px') {
      width: 90%;
    }
    @include media('>=tablet') {
      width: 80%;
    }
  }
  .setting {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
    label {
      display: flex;
      align-items: center;
      gap: 0.5em;
    }
    p {
      font-size: 0.75rem;
    }
  }
  .speed {
    display: grid;
    grid-template-rows: 1/2;
    grid-template-columns: 1/2;
    * {
      width: 100%;
      text-align: end;
      grid-row: 1;
      grid-column: 1;
    }
  }

  .slider {
    width: 100%;
  }

  .setting.disabled {
    opacity: 0.5;
    pointer-events: none;

    :global(.action) {
      border: none;
      background: none;
      padding: 0;
      pointer-events: all;
      opacity: 1;
    }
  }

  .premium-nudge {
    border: none;
    padding: 0;
    cursor: pointer;
    background: none;
    @include themify {
      color: themed('text');
    }
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .version {
    opacity: 0.6;
    text-align: end;
  }
</style>
